import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import type { Locale } from 'date-fns';
import type { TimelineColumn, TimelineZoom } from '../types';

/** Pixel width of the fixed "Persona" column. */
export const TIMELINE_PERSON_COLUMN_WIDTH_PX = 200;

/** Pixel height of a person's swimlane row. */
export const TIMELINE_ROW_HEIGHT_PX = 64;

type TimelineZoomUnit = 'day' | 'week' | 'month';

/** The underlying column unit each zoom renders — `'7d'`/`'14d'` are day columns, just wider/narrower. */
const TIMELINE_ZOOM_UNIT: Record<TimelineZoom, TimelineZoomUnit> = {
  '7d': 'day',
  '14d': 'day',
  day: 'day',
  week: 'week',
  month: 'month',
};

/**
 * Per-zoom column width, plus how many units the grid starts with and how many
 * it grows by each time the user scrolls near an edge.
 *
 * The rendered range is deliberately **not** capped: `initialBufferUnits` only
 * decides how much is ready on first paint, and `extendUnits` is appended (or
 * prepended) on demand, so panning far into the past or future keeps working
 * indefinitely rather than dead-ending at a preloaded boundary.
 */
export const TIMELINE_ZOOM_CONFIG: Record<
  TimelineZoom,
  { columnWidthPx: number; initialBufferUnits: number; extendUnits: number }
> = {
  '7d': { columnWidthPx: 170, initialBufferUnits: 90, extendUnits: 60 }, // ~7 day-columns fill the viewport
  '14d': { columnWidthPx: 90, initialBufferUnits: 90, extendUnits: 60 }, // ~14 day-columns fill the viewport
  day: { columnWidthPx: 56, initialBufferUnits: 90, extendUnits: 60 },
  week: { columnWidthPx: 160, initialBufferUnits: 52, extendUnits: 26 },
  month: { columnWidthPx: 220, initialBufferUnits: 24, extendUnits: 12 },
};

/** Shifts a window start by whole zoom units (negative = further into the past). */
export function shiftWindowStart(windowStart: Date, zoom: TimelineZoom, units: number): Date {
  switch (TIMELINE_ZOOM_UNIT[zoom]) {
    case 'day':
      return addDays(windowStart, units);
    case 'month':
      return addMonths(windowStart, units);
    case 'week':
      return addWeeks(windowStart, units);
  }
}

/**
 * How much of the initial buffer sits *before* today. A third leaves the view
 * biased toward upcoming work while still putting comfortably more than the
 * edge threshold's worth of pixels to the left — otherwise the very first
 * centered position would already be close enough to the left edge to trigger
 * an immediate, pointless extension.
 */
export function getInitialBackUnits(zoom: TimelineZoom): number {
  return Math.floor(TIMELINE_ZOOM_CONFIG[zoom].initialBufferUnits / 3);
}

/**
 * Where the grid starts on first paint. Scrolling past either end extends the
 * range from here, so this is a starting point, not a limit.
 */
export function getDefaultTimelineWindowStart(
  zoom: TimelineZoom,
  referenceDate: Date = new Date(),
): Date {
  const backUnits = getInitialBackUnits(zoom);
  switch (TIMELINE_ZOOM_UNIT[zoom]) {
    case 'day':
      return subDays(startOfDay(referenceDate), backUnits);
    case 'month':
      return subMonths(startOfMonth(referenceDate), backUnits);
    case 'week':
      return subWeeks(startOfWeek(referenceDate, { weekStartsOn: 1 }), backUnits);
  }
}

function formatWeekLabel(start: Date, end: Date, locale: Locale): string {
  const startLabel =
    start.getMonth() === end.getMonth()
      ? format(start, 'd', { locale })
      : format(start, 'd MMM', { locale });
  return `${startLabel} - ${format(end, 'd MMM', { locale })}`;
}

/** Builds `count` columns starting at `windowStart`, shaped per the active zoom's unit (day/week/month). */
export function computeColumns(
  windowStart: Date,
  zoom: TimelineZoom,
  count: number,
  locale: Locale,
): TimelineColumn[] {
  const unit = TIMELINE_ZOOM_UNIT[zoom];
  return Array.from({ length: count }, (_, index) => {
    if (unit === 'day') {
      const startDate = addDays(windowStart, index);
      return { startDate, endDate: startDate, label: format(startDate, 'd MMM', { locale }) };
    }
    if (unit === 'month') {
      const startDate = addMonths(windowStart, index);
      return {
        startDate,
        endDate: endOfMonth(startDate),
        label: format(startDate, 'MMMM yyyy', { locale }),
      };
    }
    const startDate = addWeeks(windowStart, index);
    const endDate = addDays(startDate, 6);
    return { startDate, endDate, label: formatWeekLabel(startDate, endDate, locale) };
  });
}

export interface TimelineBarPosition {
  leftPx: number;
  widthPx: number;
}

/**
 * Positions a date range as a horizontal bar within the visible window, clamped
 * to the window bounds. Returns `null` when the range has no overlap with the
 * window (nothing to render).
 */
export function computeBarPosition(
  rangeStart: Date,
  rangeEnd: Date,
  windowStart: Date,
  windowEnd: Date,
  totalWidthPx: number,
): TimelineBarPosition | null {
  if (rangeEnd < windowStart || rangeStart > windowEnd) return null;

  const totalDays = differenceInCalendarDays(windowEnd, windowStart) + 1;
  const pxPerDay = totalWidthPx / totalDays;

  const clampedStart = rangeStart < windowStart ? windowStart : rangeStart;
  const clampedEnd = rangeEnd > windowEnd ? windowEnd : rangeEnd;

  const offsetDays = differenceInCalendarDays(clampedStart, windowStart);
  const spanDays = differenceInCalendarDays(clampedEnd, clampedStart) + 1;

  return { leftPx: offsetDays * pxPerDay, widthPx: spanDays * pxPerDay };
}

/**
 * Horizontal offset (in px, from the left edge of the grid) for a single-day
 * marker — the "today" line or a milestone diamond. Returns `null` when the
 * date falls outside the visible window.
 */
export function computeMarkerOffset(
  date: Date,
  windowStart: Date,
  windowEnd: Date,
  totalWidthPx: number,
): number | null {
  if (date < windowStart || date > windowEnd) return null;

  const totalDays = differenceInCalendarDays(windowEnd, windowStart) + 1;
  const pxPerDay = totalWidthPx / totalDays;
  const offsetDays = differenceInCalendarDays(date, windowStart);

  return offsetDays * pxPerDay + pxPerDay / 2;
}

/**
 * `scrollLeft` value (for the shared scroll container) that centers `date` in
 * the viewport, accounting for the sticky "Persona" column always occupying
 * `TIMELINE_PERSON_COLUMN_WIDTH_PX` of the visible width. Clamped to a valid
 * scroll range. Falls back to `0` when `date` is outside the loaded buffer.
 */
export function computeScrollLeftForDate(
  date: Date,
  windowStart: Date,
  windowEnd: Date,
  totalWidthPx: number,
  viewportWidthPx: number,
): number {
  const offsetPx = computeMarkerOffset(date, windowStart, windowEnd, totalWidthPx) ?? 0;
  const target =
    TIMELINE_PERSON_COLUMN_WIDTH_PX +
    offsetPx -
    (viewportWidthPx - TIMELINE_PERSON_COLUMN_WIDTH_PX) / 2;
  const maxScrollLeft = Math.max(
    0,
    TIMELINE_PERSON_COLUMN_WIDTH_PX + totalWidthPx - viewportWidthPx,
  );
  return Math.min(Math.max(target, 0), maxScrollLeft);
}
